'use client';

import React from 'react';
import clsx from 'clsx';

export interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Icon to display (can be an SVG or icon component)
   */
  icon?: React.ReactNode;

  /**
   * Title text for the empty state
   */
  title: string;

  /**
   * Description or message text
   */
  description?: React.ReactNode;

  /**
   * Primary action button or link
   */
  action?: React.ReactNode;

  /**
   * Secondary action button or link
   */
  secondaryAction?: React.ReactNode;

  /**
   * Size variant for the empty state
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Additional CSS classes for the container
   */
  className?: string;
}

/**
 * EmptyState Component
 *
 * A reusable component for displaying empty states in lists, tables, or sections.
 * Shows a friendly message with optional actions when there's no content to display.
 *
 * @example
 * ```tsx
 * // Basic empty state
 * <EmptyState
 *   icon={<DocumentIcon />}
 *   title="No documents uploaded"
 *   description="Get started by uploading your first document"
 * />
 *
 * // Empty state with actions
 * <EmptyState
 *   icon={<InboxIcon />}
 *   title="No jobs found"
 *   description="Try adjusting your filters or create a new job"
 *   action={
 *     <button className="btn-primary">Create Job</button>
 *   }
 *   secondaryAction={
 *     <button className="btn-secondary">Clear Filters</button>
 *   }
 * />
 *
 * // Small size for inline use
 * <EmptyState
 *   size="sm"
 *   title="No results"
 *   description="No matching items found"
 * />
 * ```
 */
export function EmptyState({
  icon,
  title,
  description,
  action,
  secondaryAction,
  size = 'md',
  className,
  ...rest
}: EmptyStateProps) {
  // Size-specific styling
  const sizeStyles = {
    sm: {
      container: 'py-6',
      icon: 'h-8 w-8',
      title: 'text-sm font-medium',
      description: 'text-xs',
      actions: 'mt-3 gap-2',
    },
    md: {
      container: 'py-12',
      icon: 'h-12 w-12',
      title: 'text-base font-semibold',
      description: 'text-sm',
      actions: 'mt-6 gap-3',
    },
    lg: {
      container: 'py-16',
      icon: 'h-16 w-16',
      title: 'text-lg font-semibold',
      description: 'text-base',
      actions: 'mt-8 gap-4',
    },
  };

  const styles = sizeStyles[size];

  // Default icon if none provided
  const defaultIcon = (
    <svg
      className={clsx(styles.icon, 'text-gray-300')}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
      />
    </svg>
  );

  return (
    <div
      className={clsx(
        'flex flex-col items-center justify-center text-center',
        styles.container,
        className
      )}
      {...rest}
    >
      {/* Icon */}
      <div className="flex-shrink-0">
        {icon ? (
          <div className={clsx(styles.icon, 'text-gray-400')}>{icon}</div>
        ) : (
          defaultIcon
        )}
      </div>

      {/* Title */}
      <h3 className={clsx('mt-4 text-gray-900', styles.title)}>{title}</h3>

      {/* Description */}
      {description && (
        <p className={clsx('mt-2 max-w-md text-gray-500', styles.description)}>
          {description}
        </p>
      )}

      {/* Actions */}
      {(action || secondaryAction) && (
        <div className={clsx('flex flex-wrap items-center justify-center', styles.actions)}>
          {action && <div>{action}</div>}
          {secondaryAction && <div>{secondaryAction}</div>}
        </div>
      )}
    </div>
  );
}

export default EmptyState;
